/**
 * StructuredData Component
 * Adds JSON-LD structured data for search engines
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface StructuredDataProps {
  data: object;
}

export const StructuredData: React.FC<StructuredDataProps> = ({ data }) => {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(data)}
      </script>
    </Helmet>
  );
};

// Helper functions to generate common schema types

export const createOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'trAInedup.ai',
  alternateName: 'trAInedup',
  url: 'https://trainedup.ai',
  logo: 'https://trainedup.ai/tulogo.png',
  description: 'Feeling left behind by AI? Your practical, supportive path to catching up.',
  sameAs: [
    // Add social media URLs here when available
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    url: 'https://trainedup.ai/contact',
  },
});

export interface CourseSchemaProps {
  name: string;
  description: string;
  provider: string;
  url: string;
  image?: string;
  numberOfLessons?: number;
  timeRequired?: string;
  courseCode?: string;
  educationalLevel?: string;
}

export const createCourseSchema = ({
  name,
  description,
  provider,
  url,
  image = 'https://trainedup.ai/tulogo.png',
  numberOfLessons,
  timeRequired,
  courseCode,
  educationalLevel = 'Beginner to Advanced',
}: CourseSchemaProps) => ({
  '@context': 'https://schema.org',
  '@type': 'Course',
  name,
  description,
  provider: {
    '@type': 'Organization',
    name: provider,
    sameAs: 'https://trainedup.ai',
  },
  url,
  image,
  ...(numberOfLessons && { numberOfLessons }),
  ...(timeRequired && { timeRequired }),
  ...(courseCode && { courseCode }),
  educationalLevel,
  inLanguage: 'en',
});

export interface ProductSchemaProps {
  name: string;
  description: string;
  price: string;
  currency?: string;
  url?: string;
  image?: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
}

export const createProductSchema = ({
  name,
  description,
  price,
  currency = 'USD',
  url = 'https://trainedup.ai/pricing',
  image = 'https://trainedup.ai/tulogo.png',
  availability = 'InStock',
}: ProductSchemaProps) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  image,
  url,
  offers: {
    '@type': 'Offer',
    price,
    priceCurrency: currency,
    availability: `https://schema.org/${availability}`,
    url,
  },
});

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export const createBreadcrumbSchema = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export interface FAQItem {
  question: string;
  answer: string;
}

export const createFAQSchema = (faqs: FAQItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const createWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'trAInedup.ai',
  alternateName: 'trAInedup',
  url: 'https://trainedup.ai',
  description: 'Feeling left behind by AI? Your practical, supportive path to catching up.',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://trainedup.ai/courses?search={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
});
